/**
 * Helpers for matching a sale's pickup address to a company warehouse and
 * for matching a sale item's stock locations to a pickup "source" location
 * (warehouse or temp address).
 *
 * `isPickupAddressAtWarehouse` is the gate used by mutation sites
 * (UPDATE_SALE_ITEM_STATUS, functions/issues.js, functions/sales-create.js,
 * functions/sales-change.js, functions/sales.js:repairSale) to decide
 * whether to mirror `delivery.*Ready` deltas into `pickup.*Ready` so that
 * `status.pickup.ready` stays authoritative for CPU sales whose pickup
 * address is a company warehouse.
 *
 * The `isPickupLocationMatch` / `isPickupSourceLocation` /
 * `getItemQuantityAtPickupSource` helpers are used by the Pickup dialog to
 * determine per-item availability at the user's selected pickup location.
 */

import Address from "../classes/Address";
import { LOCATION_INFO, LOCATIONS } from "../constants";
import { store } from "../store";

// A location is a warehouse when an active warehouse map is assigned to it
// (warehouseMaps is the source of truth; LOCATION_INFO no longer flags it).
function hasWarehouse(locationId) {
  return Boolean(store.getters?.warehousesForLocation?.(locationId)?.length);
}

/**
 * The `LOCATION_INFO` key of the company warehouse at `address`, or `null`
 * when it is not one of ours. A delivery there is a transfer, not a final
 * delivery: EventEdit forces it to a Temporary Destination, and the Delivery
 * page and Truck Unload dialog unload it into the warehouse. All three use
 * this one check, with `Address.isSameLocation`, so they agree on which
 * deliveries those are.
 *
 * Components pass their own `this.$store.getters.warehousesForLocation`.
 */
export function getWarehouseLocationAt(
  address,
  warehousesForLocation = store.getters?.warehousesForLocation
) {
  if (!address) return null;
  const destination =
    address instanceof Address ? address : new Address(address);
  const match = Object.entries(LOCATION_INFO).find(
    ([key, location]) =>
      warehousesForLocation?.(key)?.length > 0 &&
      location.address?.isSameLocation?.(destination)
  );
  return match ? match[0] : null;
}

// Canonical short forms for common US street directionals and suffixes so
// that equivalent addresses entered in different styles (e.g. Google's
// "180 East Broadway Avenue" vs the stored "180 E Broadway Ave") normalize
// to the same key. Without this, a CPU sale whose pickup address came from
// autocomplete would fail the warehouse match and never mirror
// `delivery.ready` into `pickup.ready` (see sale 03500053A).
//
// NOTE: This is a mitigation, not a cure — string matching is still brittle.
// The deferred identity + item-location fix is documented in
// project/docs/PICKUP_WAREHOUSE_MATCHING.md.
const STREET_ABBREVIATIONS = {
  north: "n",
  south: "s",
  east: "e",
  west: "w",
  northeast: "ne",
  northwest: "nw",
  southeast: "se",
  southwest: "sw",
  avenue: "ave",
  street: "st",
  road: "rd",
  drive: "dr",
  boulevard: "blvd",
  lane: "ln",
  court: "ct",
  place: "pl",
  circle: "cir",
  highway: "hwy",
  parkway: "pkwy",
  terrace: "ter",
  trail: "trl",
  square: "sq",
  suite: "ste",
  apartment: "apt",
};

function normalizeStreet(street) {
  return String(street || "")
    .toLowerCase()
    .replace(/[.,]/g, " ")
    .split(/\s+/)
    .filter(Boolean)
    .map((token) => STREET_ABBREVIATIONS[token] || token)
    .join(" ");
}

function normalizeAddressKey(address) {
  const part = (value) =>
    String(value || "")
      .trim()
      .toLowerCase();
  return [
    normalizeStreet(address?.street),
    part(address?.development),
    part(address?.lot),
    part(address?.city),
    part(address?.state),
    part(address?.zip),
  ].join("|");
}

/**
 * Does the sale's pickup address match a company warehouse location?
 *
 * Used by `UPDATE_SALE_ITEM_STATUS` and its backend siblings to decide
 * whether QC-time (and change-order) `delivery.*Ready` deltas should also
 * be mirrored into `status.pickup.*Ready`. CPU sales whose pickup address
 * is a company warehouse have no delivery-to-pickup event to populate
 * those counters, so the mirror is what keeps them authoritative.
 *
 * Accepts either a Sale class instance or raw sale data.
 */
export function isPickupAddressAtWarehouse(sale) {
  return Boolean(getPickupWarehouseLocationValue(sale));
}

/**
 * Returns the `LOCATION_INFO` key (e.g. `"westervillewh"`, `"plaincity"`)
 * that matches the sale's pickup address, or `null` if the sale isn't a
 * CPU sale or the address doesn't match any company warehouse.
 *
 * Used by `EventEdit` to pin the Pickup Location dropdown to the single
 * warehouse that corresponds to the sale's ship-to address.
 */
export function getPickupWarehouseLocationValue(sale) {
  const saleData = sale?.data || sale;
  const address = saleData?.shipTo?.address;
  if (!saleData?.shipTo?.pickup || !address) return null;

  const key = normalizeAddressKey(address);
  const match = LOCATIONS.find(({ value }) => {
    const info = LOCATION_INFO[value];
    if (!info?.address?.data || !hasWarehouse(value)) return false;
    return key === normalizeAddressKey(info.address.data);
  });
  return match?.value || null;
}

/**
 * Does the given item location match a specific pickup location value
 * (a key in LOCATION_INFO)?
 *
 * Accepts either raw location data (`{ type, id, address, quantity }`) or an
 * ItemLocation-wrapped instance (`{ data: { ... } }`).
 */
export function isPickupLocationMatch(location, locationValue) {
  const info = LOCATION_INFO[locationValue];
  if (!info) return false;

  const locationData = location?.data || location;
  const quantity = Number(locationData?.quantity || 0);
  if (!quantity || !locationData?.type) return false;

  if (locationData.type === "temp" && locationData.address) {
    return (
      normalizeAddressKey(locationData.address) ===
      normalizeAddressKey(info.address.data)
    );
  }

  if (locationData.type === "warehouse" && locationData.id) {
    const warehouse = String(locationData.id).split(":")[0];
    const warehouses = store.getters?.warehousesForLocation?.(locationValue);
    return Boolean(warehouses?.includes(warehouse));
  }

  return false;
}

/**
 * Does the given item location match ANY pickup source location?
 */
export function isPickupSourceLocation(location) {
  return LOCATIONS.some(({ value }) => isPickupLocationMatch(location, value));
}

/**
 * Total quantity of an item available at the pickup source location.
 *
 * If `locationValue` is provided, counts only that specific pickup location
 * (key in LOCATION_INFO). Otherwise counts across all pickup source
 * locations.
 */
export function getItemQuantityAtPickupSource(item, locationValue = null) {
  if (!item || !Array.isArray(item.itemLocation)) return 0;

  return item.itemLocation.reduce((sum, location) => {
    const locationData = location?.data || location;
    const quantity = Number(locationData?.quantity || 0);
    if (quantity <= 0) return sum;

    const isMatch = locationValue
      ? isPickupLocationMatch(location, locationValue)
      : isPickupSourceLocation(location);
    return isMatch ? sum + quantity : sum;
  }, 0);
}

/**
 * Does a pickup sale have any items with stock at a pickup source location?
 *
 * Used to surface the Pickup button / allow scheduling for CPU sales whose
 * items sit at the pickup-address warehouse without any intervening delivery
 * event having populated `status.pickup.ready`. Mirrors the availability
 * check used inside the Pickup dialog's item selector (which intentionally
 * ignores `quantityScheduled` because it is shared with delivery events).
 */
export function saleHasItemsReadyAtPickupLocation(items) {
  if (!items || !items.length) return false;
  return items.some((item) => getItemQuantityAtPickupSource(item) > 0);
}
